import React, { useEffect, useMemo, useState } from 'react';
import { Activity, AppWindow, Cpu, Gauge, MemoryStick, Monitor, XCircle } from 'lucide-react';
import { makeOverrideIcon } from '../AppIcon';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { useSystemMonitor } from '../../hooks/useSystemMonitor';

type ProcessInfo = {
  id: string;
  title: string;
  appId: string;
  zIndex: number;
  width: number;
  height: number;
  hidden: boolean;
};

type MetricHistory = {
  fps: number[];
  frameTime: number[];
  cpu: number[];
  memory: number[];
};

const HISTORY_POINTS = 36;

const CpuMetricIcon = makeOverrideIcon('cpu', Cpu);
const RamMetricIcon = makeOverrideIcon('ram', MemoryStick);
const GpuMetricIcon = makeOverrideIcon('gpu', Monitor);

const formatMb = (value: number | null) => (value == null ? 'Unavailable' : `${value.toLocaleString()} MB`);
const pushHistory = (values: number[], next: number) => [...values.slice(-(HISTORY_POINTS - 1)), next];

const MetricCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  subvalue: string;
  progress?: number;
}> = ({ icon, label, value, subvalue, progress }) => (
  <Card className="border-border/60 bg-card/70 shadow-[0_18px_50px_-24px_rgba(0,0,0,0.45)] backdrop-blur-xl">
    <CardContent className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">{label}</div>
          <div className="mt-2 text-2xl font-semibold tracking-tight">{value}</div>
          <div className="mt-1 line-clamp-2 text-sm text-muted-foreground">{subvalue}</div>
        </div>
        <div className="shrink-0 pt-0.5 text-primary">{icon}</div>
      </div>
      {typeof progress === 'number' ? <Progress value={progress} className="mt-4 h-2" /> : null}
    </CardContent>
  </Card>
);

const LiveLineGraph: React.FC<{
  values: number[];
  maxValue: number;
  stroke: string;
}> = ({ values, maxValue, stroke }) => {
  const width = 100;
  const height = 44;
  const safeMax = Math.max(maxValue, 1);

  const points = values
    .map((value, index) => {
      const x = values.length === 1 ? 0 : (index / (values.length - 1)) * width;
      const y = height - (Math.max(0, Math.min(value, safeMax)) / safeMax) * height;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <div className="rounded-xl border border-border/50 bg-background/50 p-2">
      <svg viewBox={`0 0 ${width} ${height}`} className="h-16 w-full overflow-visible">
        <path d={`M0 ${height} L${points} L${width} ${height} Z`} fill={stroke} opacity="0.12" />
        <polyline
          fill="none"
          stroke={stroke}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={points}
        />
      </svg>
    </div>
  );
};

const PerformanceMiniChart: React.FC<{
  label: string;
  value: string;
  helper: string;
  values: number[];
  maxValue: number;
  stroke: string;
  disabled?: boolean;
}> = ({ label, value, helper, values, maxValue, stroke, disabled = false }) => (
  <div className="rounded-2xl border border-border/50 bg-background/60 p-3">
    <div className="mb-2 flex items-end justify-between gap-3">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
        <div className="mt-1 text-lg font-semibold leading-none">{value}</div>
      </div>
      <div className="text-xs text-muted-foreground">{helper}</div>
    </div>
      {disabled ? (
        <div className="flex h-[88px] items-center justify-center rounded-xl border border-dashed border-border/50 bg-background/40 text-xs text-muted-foreground">
          Available in Electron desktop
        </div>
      ) : (
        <LiveLineGraph values={values} maxValue={maxValue} stroke={stroke} />
      )}
  </div>
);

const TaskManager: React.FC = () => {
  const snapshot = useSystemMonitor();
  const [processes, setProcesses] = useState<ProcessInfo[]>([]);
  const [history, setHistory] = useState<MetricHistory>({
    fps: Array.from({ length: HISTORY_POINTS }, () => 60),
    frameTime: Array.from({ length: HISTORY_POINTS }, () => 16),
    cpu: Array.from({ length: HISTORY_POINTS }, () => 0),
    memory: Array.from({ length: HISTORY_POINTS }, () => 10),
  });

  useEffect(() => {
    const collectProcesses = () => {
      const rows = Array.from(document.querySelectorAll<HTMLElement>('[data-window-id]')).map((element) => {
        const rect = element.getBoundingClientRect();
        const style = window.getComputedStyle(element);

        return {
          id: element.dataset.windowId || 'unknown-window',
          title: element.dataset.title || 'Untitled window',
          appId: element.dataset.appId || 'unknown-app',
          zIndex: Number(style.zIndex || 0),
          width: Math.round(rect.width),
          height: Math.round(rect.height),
          hidden: style.display === 'none' || style.visibility === 'hidden',
        };
      });

      rows.sort((a, b) => b.zIndex - a.zIndex);
      setProcesses(rows);
    };

    collectProcesses();
    const interval = window.setInterval(collectProcesses, 1200);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const memorySeriesValue = snapshot.memory.systemUsagePercent ?? snapshot.memory.heapUsagePercent;

    setHistory((prev) => ({
      fps: pushHistory(prev.fps, snapshot.runtime.fps),
      frameTime: pushHistory(prev.frameTime, snapshot.runtime.frameTimeMs),
      cpu: pushHistory(prev.cpu, snapshot.cpu.usagePercent ?? 0),
      memory: pushHistory(prev.memory, memorySeriesValue),
    }));
  }, [snapshot.runtime.fps, snapshot.runtime.frameTimeMs, snapshot.cpu.usagePercent, snapshot.memory.systemUsagePercent, snapshot.memory.heapUsagePercent]);

  const topLine = useMemo(() => {
    if (snapshot.gpu.systemModel) {
      return `Desktop GPU • ${snapshot.gpu.systemModel}`;
    }
    if (snapshot.gpu.webgpuSupported && snapshot.gpu.webgpuAdapterName) {
      return `WebGPU ready • ${snapshot.gpu.webgpuAdapterName}`;
    }

    return 'WebGPU not exposed in this browser session';
  }, [snapshot.gpu.systemModel, snapshot.gpu.webgpuAdapterName, snapshot.gpu.webgpuSupported]);

  const cpuCardValue = snapshot.cpu.available ? `${snapshot.cpu.usagePercent}%` : '—';
  const cpuCardSubvalue = snapshot.cpu.available
    ? `${snapshot.cpu.cores ?? 'Unknown'} logical cores • real system usage`
    : 'Browser mode cannot provide accurate CPU telemetry.';
  const memoryCardValue = `${snapshot.memory.systemUsagePercent ?? snapshot.memory.heapUsagePercent}%`;
  const memoryCardSubvalue = snapshot.memory.systemUsagePercent != null
    ? `${formatMb(snapshot.memory.systemUsedMb)} used of ${formatMb(snapshot.memory.systemTotalMb)}`
    : `${formatMb(snapshot.memory.usedHeapMb)} used of ${formatMb(snapshot.memory.heapLimitMb)}`;
  const gpuCardValue = snapshot.gpu.systemUsagePercent != null ? `${snapshot.gpu.systemUsagePercent}%` : snapshot.gpu.webglRenderer.includes('unavailable') ? 'Unavailable' : 'Detected';
  const gpuCardSubvalue = snapshot.gpu.systemModel
    ? [
        snapshot.gpu.systemModel,
        snapshot.gpu.vramUsedMb != null && snapshot.gpu.vramTotalMb != null
          ? `${snapshot.gpu.vramUsedMb.toLocaleString()}/${snapshot.gpu.vramTotalMb.toLocaleString()} MB VRAM`
          : snapshot.gpu.temperatureC != null
            ? `${snapshot.gpu.temperatureC}°C`
            : null,
      ].filter(Boolean).join(' • ')
    : snapshot.gpu.webglRenderer;

  const handleForceClose = (process: ProcessInfo) => {
    window.dispatchEvent(
      new CustomEvent('mapleos:force-close-window', {
        detail: { windowId: process.id },
      })
    );

    setProcesses((prev) => prev.filter((item) => item.id !== process.id));
  };

  return (
    <div className="relative h-full overflow-y-auto bg-gradient-to-br from-background via-background to-muted/20">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-24 top-0 h-72 w-72 rounded-full bg-primary/12 blur-3xl" />
        <div className="absolute right-0 top-10 h-80 w-80 rounded-full bg-accent/30 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
      </div>

      <div className="relative flex min-h-full flex-col">
        <div className="border-b border-border/60 bg-card/60 px-4 py-4 backdrop-blur-xl">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3">
                <div className="p-0.5 text-primary">
                  <Activity className="h-7 w-7" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-2xl font-semibold tracking-tight">Task Manager</h1>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {snapshot.runtime.environment === 'electron'
                      ? 'Desktop mode with real OS-level telemetry.'
                      : 'Browser mode with runtime and graphics telemetry.'}
                  </p>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="gap-1 rounded-full px-2.5 py-1 text-xs">
                  <Gauge className="h-3.5 w-3.5" /> {snapshot.runtime.fps} FPS
                </Badge>
                <Badge variant="outline" className="rounded-full px-2.5 py-1 text-xs">
                  {snapshot.runtime.activeWindows} active windows
                </Badge>
                <Badge variant="outline" className="rounded-full px-2.5 py-1 text-xs">
                  {snapshot.runtime.environment === 'electron' ? 'Electron desktop' : 'Browser sandbox'}
                </Badge>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              <div className="max-w-[360px] rounded-xl border border-border/60 bg-background/70 px-3.5 py-2.5 text-right shadow-sm">
                <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">Runtime signal</div>
                <div className="mt-1 text-sm font-medium leading-5">{topLine}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-3 px-4 pb-4 pt-4 md:grid-cols-2 xl:grid-cols-3">
          <MetricCard
            icon={<CpuMetricIcon size={26} className="h-[26px] w-[26px] text-primary" />}
            label="CPU"
            value={cpuCardValue}
            subvalue={cpuCardSubvalue}
            progress={snapshot.cpu.available ? snapshot.cpu.usagePercent ?? undefined : undefined}
          />
          <MetricCard
            icon={<RamMetricIcon size={26} className="h-[26px] w-[26px] text-primary" />}
            label="Memory"
            value={memoryCardValue}
            subvalue={memoryCardSubvalue}
            progress={snapshot.memory.systemUsagePercent ?? snapshot.memory.heapUsagePercent}
          />
          <MetricCard
            icon={<GpuMetricIcon size={26} className="h-[26px] w-[26px] text-primary" />}
            label="GPU"
            value={gpuCardValue}
            subvalue={gpuCardSubvalue}
            progress={snapshot.gpu.systemUsagePercent ?? undefined}
          />
        </div>

        <div className="px-4 pb-4">
          <Card className="border-border/60 bg-card/70 shadow-[0_18px_50px_-24px_rgba(0,0,0,0.45)] backdrop-blur-xl">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Gauge className="h-5 w-5 text-primary" /> Live performance graph
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 pt-0 md:grid-cols-2 xl:grid-cols-4">
              <PerformanceMiniChart
                label="FPS"
                value={`${snapshot.runtime.fps}`}
                helper="frames/sec"
                values={history.fps}
                maxValue={120}
                stroke="hsl(var(--primary))"
              />
              <PerformanceMiniChart
                label="Frame time"
                value={`${snapshot.runtime.frameTimeMs.toFixed(1)} ms`}
                helper="render cost"
                values={history.frameTime}
                maxValue={40}
                stroke="#8b5cf6"
              />
              <PerformanceMiniChart
                label="CPU"
                value={snapshot.cpu.available ? `${snapshot.cpu.usagePercent}%` : '—'}
                helper={snapshot.cpu.available ? 'system usage' : 'desktop only'}
                values={history.cpu}
                maxValue={100}
                stroke="#22c55e"
                disabled={!snapshot.cpu.available}
              />
              <PerformanceMiniChart
                label="Memory"
                value={`${snapshot.memory.systemUsagePercent ?? snapshot.memory.heapUsagePercent}%`}
                helper={snapshot.memory.systemUsagePercent != null ? 'system RAM' : 'heap usage'}
                values={history.memory}
                maxValue={100}
                stroke="#f59e0b"
              />
            </CardContent>
          </Card>
        </div>

        <div className="px-4 pb-4">
          <Card className="border-border/60 bg-card/70 shadow-[0_18px_50px_-24px_rgba(0,0,0,0.45)] backdrop-blur-xl">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <AppWindow className="h-5 w-5 text-primary" /> Active MapleOS windows
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 p-4 pt-0">
              {processes.map((process) => (
                <div key={process.id} className="grid gap-2 rounded-xl border border-border/50 bg-background/60 p-3 lg:grid-cols-[minmax(0,1.4fr)_auto_auto_auto_auto] lg:items-center">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="truncate font-semibold">{process.title}</div>
                      <Badge variant={process.hidden ? 'secondary' : 'outline'} className="h-5 px-2 text-[10px]">
                        {process.hidden ? 'Hidden' : 'Visible'}
                      </Badge>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">{process.appId} • {process.id}</div>
                  </div>
                  <div className="rounded-lg border border-border/50 bg-card/70 px-3 py-2 text-center text-xs">
                    <div className="uppercase tracking-[0.14em] text-muted-foreground">Size</div>
                    <div className="mt-1 font-semibold text-sm">{process.width}×{process.height}</div>
                  </div>
                  <div className="rounded-lg border border-border/50 bg-card/70 px-3 py-2 text-center text-xs">
                    <div className="uppercase tracking-[0.14em] text-muted-foreground">Z</div>
                    <div className="mt-1 font-semibold text-sm">{process.zIndex}</div>
                  </div>
                  <div className="rounded-lg border border-border/50 bg-card/70 px-3 py-2 text-center text-xs">
                    <div className="uppercase tracking-[0.14em] text-muted-foreground">State</div>
                    <div className="mt-1 font-semibold text-sm">{process.hidden ? 'Parked' : 'Live'}</div>
                  </div>
                  <div className="flex justify-end lg:justify-start">
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="min-w-[108px] gap-2"
                      onClick={() => handleForceClose(process)}
                    >
                      <XCircle className="h-4 w-4" />
                      End task
                    </Button>
                  </div>
                </div>
              ))}

              {processes.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border/60 bg-background/40 p-6 text-center text-sm text-muted-foreground">
                  No MapleOS windows detected.
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TaskManager;